<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { isMobileViewport, prefersReducedMotion } from '../../utils/environment'
import { ASSETS } from './constants'

const scrolled = ref(false)
/** 移动端不展示小红书二维码 */
const xhsVisible = ref(!isMobileViewport())
const VISIBILITY_HEIGHT = 400

const onScroll = () => {
  scrolled.value = window.scrollY > VISIBILITY_HEIGHT
}

const scrollToTop = () => {
  const reduce = prefersReducedMotion()
  window.scrollTo({
    top: 0,
    behavior: reduce ? 'auto' : 'smooth'
  })
}

/** 仅当前页关闭，刷新后重新展示 */
const closeXhs = () => {
  xhsVisible.value = false
}

onMounted(() => {
  if (isMobileViewport()) xhsVisible.value = false
  onScroll()
  window.addEventListener('scroll', onScroll, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <div class="side-dock">
    <!-- 小红书二维码：可关闭，刷新后恢复 -->
    <Transition name="dock-xhs">
      <aside v-if="xhsVisible" class="dock-xhs-panel" aria-label="关注小红书">
        <button
          type="button"
          class="dock-xhs-close"
          aria-label="关闭小红书二维码"
          @click="closeXhs"
        >
          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
            <path
              d="M3.5 3.5l9 9M12.5 3.5l-9 9"
              fill="none"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <div class="dock-xhs-title">关注小红书</div>
        <div class="dock-xhs-sub">获得最新消息</div>
        <div class="dock-xhs-qr">
          <img :src="ASSETS.xhsQr" alt="美团智播小红书二维码" width="100" height="100">
        </div>
      </aside>
    </Transition>

    <!-- 返回顶部：下滑后出现 -->
    <Transition name="dock-top">
      <button
        v-if="scrolled"
        type="button"
        class="dock-top-btn"
        aria-label="返回顶部"
        @click="scrollToTop"
      >
        <svg class="dock-top-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path
            d="M6 7.5h12M12 10.5v8M12 10.5l-3.2 3.2M12 10.5l3.2 3.2"
            fill="none"
            stroke="#171717"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.side-dock {
  position: fixed;
  z-index: 40;
  right: 32px;
  bottom: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.dock-xhs-panel {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 140px;
  padding: 20px 12px 16px;
  border-radius: 16px;
  background: #f7f7f7;
  box-shadow:
    0 8px 28px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.04);
  text-align: center;
}

.dock-xhs-close {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #999;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;
}

.dock-xhs-close:hover {
  color: #171717;
  background: rgba(0, 0, 0, 0.06);
}

.dock-xhs-title {
  color: #171717;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
}

.dock-xhs-sub {
  margin-top: 6px;
  color: #666;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
}

.dock-xhs-qr {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 108px;
  margin-top: 12px;
  border: 1px dashed #d8d8d8;
  border-radius: 8px;
  background: #fff;
}

.dock-xhs-qr img {
  display: block;
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.dock-top-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #fff;
  box-shadow:
    0 4px 18px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.dock-top-btn:hover {
  background: #f5f5f5;
}

.dock-top-btn:active {
  transform: scale(0.96);
}

.dock-top-icon {
  display: block;
}

.dock-xhs-enter-active,
.dock-xhs-leave-active,
.dock-top-enter-active,
.dock-top-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.dock-xhs-enter-from,
.dock-xhs-leave-to,
.dock-top-enter-from,
.dock-top-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (max-width: 960px) {
  .side-dock {
    right: 12px;
    bottom: 20px;
    gap: 10px;
  }

  .dock-xhs-panel {
    display: none;
  }

  .dock-top-btn {
    width: 44px;
    height: 44px;
  }
}
</style>
